import React, { useEffect, useState, useRef, Component } from 'react';
import { FormEvent } from 'react';
import { MailIcon, MessageCircleIcon, MessageSquareTextIcon, PhoneIcon, SendIcon, SmartphoneIcon, UserIcon, XIcon } from 'lucide-react';
import { toast } from 'sonner';
import { getSupabaseErrorMessage, loadPublicSettings } from '../data/repository';
import { AdminSettings } from '../data/types';
type ContactMessage = {
  name: string;
  email: string;
  message: string;
};
const initialSettings: AdminSettings = {
  termsText: '',
  contactInfo: {
    name: '',
    phone: '',
    email: ''
  }
};
export function Contact() {
  const [settings, setSettings] = useState(initialSettings);
  const [error, setError] = useState('');
  const [pendingMessage, setPendingMessage] = useState<ContactMessage | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    void loadPublicSettings().then(setSettings).catch(loadError => setError(getSupabaseErrorMessage(loadError)));
  }, []);
  useEffect(() => {
    if (!pendingMessage) return;
    closeButtonRef.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPendingMessage(null);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [pendingMessage]);
  const phone = settings.contactInfo.phone;
  const cleanPhone = phone.replace(/\D/g, '');
  const internationalPhone = normalizeCroatianPhone(phone);
  const whatsappPhone = internationalPhone;
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const contactMessage = {
      name: String(form.get('name') || '').trim(),
      email: String(form.get('email') || '').trim(),
      message: String(form.get('message') || '').trim()
    };
    if (internationalPhone) {
      setPendingMessage(contactMessage);
      return;
    }
    if (settings.contactInfo.email) {
      toast.success('Otvaramo vaš e-mail program s pripremljenom porukom.');
      window.location.href = buildContactEmailUrl(settings.contactInfo.email, contactMessage);
      return;
    }
    toast.error('Kontakt podaci trenutačno nisu dostupni. Pokušajte ponovno kasnije.');
  };
  const openChannel = (channel: 'whatsapp' | 'viber' | 'sms') => {
    if (!pendingMessage || !internationalPhone) return;
    const text = buildContactMessage(pendingMessage);
    const url = channel === 'whatsapp' ? buildWhatsAppContactUrl(whatsappPhone, text) : channel === 'viber' ? buildViberContactUrl(internationalPhone, text) : buildSmsContactUrl(internationalPhone, text);
    setPendingMessage(null);
    toast.success(channel === 'viber' ? 'Otvaramo Viber razgovor s JumpLandom u novoj kartici.' : 'Otvaramo pripremljenu poruku u novoj kartici.');
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  return <div className="min-h-screen bg-background py-12" data-id="element-525" data-mp="2rdbu40wamr0b">
      <div className="mx-auto max-w-6xl px-4" data-id="element-526" data-mp="2w5v4kozop60w">
        <header className="mb-12 text-center" data-id="element-527" data-mp="2w6o9dkf31lge">
          <h1 className="text-5xl font-display font-bold text-gray-900" data-id="element-528" data-mp="2hgz0t44nuf63">
            Kontaktirajte nas
          </h1>
          <p className="mt-4 text-xl text-gray-600" data-id="element-529" data-mp="2mglf7gw861gh">
            Imate pitanja ili trebate pomoć pri odabiru? Slobodno nas
            kontaktirajte.
          </p>
        </header>

        {error && <p role="alert" className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-red-800" data-id="element-530" data-mp="3f2wf0pc0byc8">
            {error}
          </p>}

        <div className="grid gap-8 lg:grid-cols-2" data-id="element-531" data-mp="3559w7rrl7mmz">
          <section className="rounded-3xl border border-gray-100 bg-white p-8" data-id="element-532" data-mp="3584ebcn4ovd8">
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-533" data-mp="1pnzoytzgz71y">Kontakt podaci</h2>
            <div className="mt-7 space-y-6" data-id="element-534" data-mp="1trnyhi7nwcpu">
              <ContactRow icon={<UserIcon data-id="element-536" data-mp="29yamrwbcrowf" />} label="Kontakt osoba" value={settings.contactInfo.name || 'JumpLand'} data-id="element-535" data-mp="1tojp86fcaqsb" />
              {phone && <a href={`tel:${cleanPhone}`} data-id="element-537" data-mp="2087njedn1epn">
                  <ContactRow icon={<PhoneIcon data-id="element-539" data-mp="205v40ws8ewah" />} label="Mobitel / WhatsApp" value={phone} data-id="element-538" data-mp="208zicmzev8ev" />
                </a>}
              {settings.contactInfo.email && <a href={`mailto:${settings.contactInfo.email}`} data-id="element-540" data-mp="2ny1whkuu6j4m">
                  <ContactRow icon={<MailIcon data-id="element-542" data-mp="2iyfmdkab1j01" />} label="Email" value={settings.contactInfo.email} data-id="element-541" data-mp="2nvph9fggqmv9" />
                </a>}
            </div>
            {whatsappPhone && <a href={`https://wa.me/${whatsappPhone}`} target="_blank" rel="noreferrer" className="mt-8 inline-flex rounded-2xl bg-[#25D366] px-6 py-3 font-bold text-white transition-colors hover:bg-[#1ebe5d] focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:ring-offset-2" data-id="element-543" data-mp="17klfntxdnnwz">
                Pošalji WhatsApp
              </a>}
          </section>

          <form onSubmit={submit} className="space-y-5 rounded-3xl border border-gray-100 bg-white p-8" data-id="element-544" data-mp="3o45n824v512x">
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-545" data-mp="1r7828d5dt0n">
              Pošaljite nam poruku
            </h2>
            <Field label="Ime i prezime *" data-id="element-546" data-mp="6o5x4chyf9rz">
              <input name="name" required autoComplete="name" className="input" data-id="element-547" data-mp="6rbknnaxq3ll" />
            </Field>
            <Field label="Email *" data-id="element-548" data-mp="fpdsomt35wid">
              <input name="email" type="email" required autoComplete="email" className="input" data-id="element-549" data-mp="2cip4dy3wyop8" />
            </Field>
            <Field label="Poruka *" data-id="element-550" data-mp="3krxtej357l8h">
              <textarea name="message" required rows={5} className="input" data-id="element-551" data-mp="3kszbv8mhna08" />
            </Field>
            <button className="flex w-full items-center justify-center gap-2 rounded-2xl bg-gray-900 py-4 font-bold text-white transition-colors hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2" data-id="element-552" data-mp="3fuxle2mqg8eq">
              <SendIcon size={18} data-id="element-553" data-mp="7lxoa4eaup96" />
              Pošalji poruku
            </button>
            <p className="text-center text-xs text-gray-500" data-id="element-554" data-mp="7jtcl0a5slyc">
              Nakon klika odabrat ćete način slanja poruke.
            </p>
          </form>
        </div>
      </div>

      {pendingMessage && <div className="fixed inset-0 z-[60] flex items-end bg-gray-950/45 p-4 sm:items-center sm:justify-center" role="presentation" onMouseDown={event => {
      if (event.target === event.currentTarget) setPendingMessage(null);
    }} data-id="element-555" data-mp="gg2ceg21tml0">
          <section role="dialog" aria-modal="true" aria-labelledby="contact-channel-title" aria-describedby="contact-channel-description" className="w-full max-w-lg rounded-3xl border border-cyan-100 bg-white p-6 shadow-2xl sm:p-8" data-id="element-556" data-mp="lbhkerddko9i">
            <div className="flex items-start justify-between gap-4" data-id="element-557" data-mp="v3an7frg3pwg">
              <div data-id="element-558" data-mp="zygd0pnyxopt">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary" data-id="element-559" data-mp="101udfsd2nnum">
                  <SendIcon size={22} data-id="element-560" data-mp="qzcsrid2f39q" />
                </span>
                <h2 id="contact-channel-title" className="mt-4 text-2xl font-display font-bold text-gray-900" data-id="element-561" data-mp="r0nsn73e3qvk">
                  Kako želite poslati poruku?
                </h2>
                <p id="contact-channel-description" className="mt-2 text-sm leading-6 text-gray-600" data-id="element-562" data-mp="39qqgm1ec7xi">
                  Vaša poruka je pripremljena. Odaberite aplikaciju i potvrdite
                  slanje u njoj.
                </p>
              </div>
              <button ref={closeButtonRef} type="button" onClick={() => setPendingMessage(null)} aria-label="Zatvori izbor načina slanja" className="rounded-xl p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary" data-id="element-563" data-mp="is1k1jq6fapl">
                <XIcon size={22} data-id="element-564" data-mp="4tttpunnb7ne" />
              </button>
            </div>

            <div className="mt-7 space-y-3" data-id="element-565" data-mp="3w5db1te6b9gr">
              <ChannelButton icon={<MessageCircleIcon size={23} data-id="element-567" data-mp="3r8d65lt28cr8" />} label="WhatsApp" description="Poruka ide izravno na JumpLand broj." className="border-[#25D366]/30 bg-[#25D366]/10 text-[#157f3b] hover:bg-[#25D366]/20 focus:ring-[#25D366]" onClick={() => openChannel('whatsapp')} data-id="element-566" data-mp="3r7bqt550h43p" />
              <ChannelButton icon={<MessageSquareTextIcon size={23} data-id="element-569" data-mp="1sqjdavz3v3b4" />} label="Viber" description="Otvara Viber razgovor izravno s JumpLand brojem." className="border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100 focus:ring-violet-500" onClick={() => openChannel('viber')} data-id="element-568" data-mp="3da6i0xdwxr4r" />
              <ChannelButton icon={<SmartphoneIcon size={23} data-id="element-571" data-mp="192272qppdlyu" />} label="SMS" description="Otvara SMS s JumpLand brojem i pripremljenom porukom." className="border-cyan-200 bg-cyan-50 text-cyan-700 hover:bg-cyan-100 focus:ring-cyan-500" onClick={() => openChannel('sms')} data-id="element-570" data-mp="26ngfftkoc77b" />
            </div>

            <button type="button" onClick={() => setPendingMessage(null)} className="mt-6 w-full rounded-2xl border border-gray-200 px-5 py-3 text-sm font-bold text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2" data-id="element-572" data-mp="1ns9mexkeyovb">
              Natrag na poruku
            </button>
          </section>
        </div>}
    </div>;
}
function buildContactMessage({
  name,
  email,
  message
}: ContactMessage): string {
  return ['Nova poruka s JumpLand web stranice', '', `Ime: ${name}`, `Email: ${email}`, '', 'Poruka:', message].join('\n');
}
function normalizeCroatianPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('00')) return digits.slice(2);
  return digits.startsWith('0') ? `385${digits.slice(1)}` : digits;
}
function buildWhatsAppContactUrl(phone: string, message: string): string {
  return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
}
function buildViberContactUrl(phone: string, message: string): string {
  return `viber://chat?number=${encodeURIComponent(`+${phone}`)}&draft=${encodeURIComponent(message)}`;
}
function buildSmsContactUrl(phone: string, message: string): string {
  return `sms:+${phone}?body=${encodeURIComponent(message)}`;
}
function buildContactEmailUrl(recipient: string, contactMessage: ContactMessage): string {
  const subject = 'Nova poruka s JumpLand web stranice';
  return `mailto:${recipient}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(buildContactMessage(contactMessage))}`;
}
function ContactRow({
  icon,
  label,
  value
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return <div className="flex gap-4" data-id="element-573" data-mp="1hz3o1whkfs3l">
      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary" data-id="element-574" data-mp="1dyt7xr027lb0">
        {icon}
      </span>
      <div data-id="element-575" data-mp="191024z2o4tb9">
        <p className="text-sm font-bold text-gray-500" data-id="element-576" data-mp="1919v8vj0fgl6">{label}</p>
        <p className="mt-1 text-lg font-bold text-gray-900" data-id="element-577" data-mp="140vk34m43qix">{value}</p>
      </div>
    </div>;
}
function ChannelButton({
  icon,
  label,
  description,
  className,
  onClick
}: {
  icon: React.ReactNode;
  label: string;
  description: string;
  className: string;
  onClick: () => void;
}) {
  return <button type="button" onClick={onClick} className={`flex w-full items-center gap-4 rounded-2xl border p-4 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${className}`} data-id="element-578" data-mp="ud9rksrehtn5">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/80 shadow-sm" data-id="element-579" data-mp="kfc3c59jjwuj">
        {icon}
      </span>
      <span data-id="element-580" data-mp="1d3hp8vcbzrvy">
        <span className="block font-bold" data-id="element-581" data-mp="1d40xtlg3d3xb">{label}</span>
        <span className="mt-1 block text-xs leading-5 text-gray-600" data-id="element-582" data-mp="1d2pshh6zzo43">
          {description}
        </span>
      </span>
    </button>;
}
function Field({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <label className="block text-sm font-bold text-gray-700" data-id="element-583" data-mp="1r2qux12wv3lo">
      <span className="mb-2 block" data-id="element-584" data-mp="1r161jesc1qpe">{label}</span>
      {children}
    </label>;
}